import React, { useState } from 'react'
import { useSelector } from 'react-redux'

import ReportSkeleton from './ReportSkeleton'
import ErrorMessage from '../../../../components/Form/ErrorMessage'

import SocialValueByStakeholders from '../SroiGraphs/RedesignedSroiGraphs/SocialValueByStakeholders'
import ImpactByResidentialStatus from '../SroiGraphs/RedesignedSroiGraphs/ImpactByResidentialStatus'
import SocialValueByAgeGroup from '../SroiGraphs/RedesignedSroiGraphs/SocialValueByAgeGroup'
// import ImpactByDisability from '../SroiGraphs/RedesignedSroiGraphs/ImpactByDisability'
// import ImpactByIndexOfMultipleDeprivation from '../SroiGraphs/RedesignedSroiGraphs/ImpactByIndexOfMultipleDeprivation'
import ImpactPathways from '../SroiGraphs/RedesignedSroiGraphs/ImpactPathways'
import CapitalImpact from '../SroiGraphs/RedesignedSroiGraphs/CapitalImpact'
// import WellbeingStatistics from '../SroiGraphs/RedesignedSroiGraphs/WellbeingStatistics'
import HeadcountAndPerStakeholderImpact from '../SroiGraphs/RedesignedSroiGraphs/HeadcountAndPerStakeholderImpact'
import MappingOfImpactToSdgs from '../SroiGraphs/RedesignedSroiGraphs/MappingOfImpactToSdgs'
import MappingOfImpactToThemes from '../SroiGraphs/RedesignedSroiGraphs/MappingOfImpactToThemes'
import ImpactByRegion from '../SroiGraphs/RedesignedSroiGraphs/ImpactByRegion'
import GenderGraph from '../SroiGraphs/RedesignedSroiGraphs/GenderGraph'
import HiddenExportCopy from '../components/HiddenExportCopy'

import { useSroiFilters } from '../SroiFiltersContext'
import { useSroiReportVisibility } from '../hooks/useSroiReportVisibility'
import { useChartExportMode } from '../hooks/useChartExportMode'

import {
  getSocialReturnOnInvestmentReportErrorSelector,
  socialReturnOnInvestmentReportSelector,
  socialReturnOnInvestmentReportWasLoadedSelector
} from '../../../../modules/selectors/socialReturnOnInvestment'

import { chartModes } from '../SroiGraphs/RedesignedSroiGraphs/SocialValueByStakeholders/fields'
import { COUNTRY_ID } from '../forms/fields'
import { gridCharts } from '../constants'

import useStyles from './styles'

const ReportContent = () => {
  const classes = useStyles()

  const socialReturnOnInvestmentReportWasLoaded = useSelector(socialReturnOnInvestmentReportWasLoadedSelector)
  const getSocialReturnOnInvestmentReportError = useSelector(getSocialReturnOnInvestmentReportErrorSelector)
  const { [COUNTRY_ID]: countryOfOperation } = useSelector(socialReturnOnInvestmentReportSelector)

  const { selectedStakeholder, selectedRegion } = useSroiFilters()
  const { hasMultipleRegions } = useSroiReportVisibility()

  // Export mode management for charts
  const { isPdfGenerating, isPngExporting, onExportStart, onExportEnd, onChartReady } = useChartExportMode()

  // Export-related props for charts
  const mainChartProps = { onExportStart, onExportEnd }
  const pngChartProps = { onExportStart, onExportEnd, onChartReady, isExportCopy: true }

  // SocialValueByStakeholders chart mode state (lifted to parent to share between original and export copies)
  // This ensures that PDF/PNG exports show the same tab selection as the visible chart
  const [stakeholdersChartMode, setStakeholdersChartMode] = useState(chartModes.total)

  // Filter state
  const hasNoFiltersApplied = !selectedStakeholder && !selectedRegion

  // Show SocialValueByStakeholders when no filters are applied
  const showSocialValueByStakeholders = hasNoFiltersApplied

  // Show HeadcountAndPerStakeholderImpact when no filters are applied
  const showHeadcountChart = hasNoFiltersApplied

  // During PDF export: move chart to full-width (hide original)
  // During PNG export: keep original visible + show hidden copy in full-width
  const showStakeholdersInGrid = showSocialValueByStakeholders && !isPdfGenerating
  const showStakeholdersForPdf = showSocialValueByStakeholders && isPdfGenerating
  const showStakeholdersForPng = showSocialValueByStakeholders && isPngExporting

  if (!socialReturnOnInvestmentReportWasLoaded && !getSocialReturnOnInvestmentReportError) {
    return <ReportSkeleton />
  }

  if (getSocialReturnOnInvestmentReportError) {
    return <ErrorMessage className={classes.errorMessage} error={getSocialReturnOnInvestmentReportError.message} />
  }

  return (
    <>
      <div id="sroi-charts-container" className={classes.graphsContainer}>
        <ImpactPathways />
        <CapitalImpact />
        {showHeadcountChart && (
          // Hide on filters: regional data lacks detailed impact pathway breakdown
          <HeadcountAndPerStakeholderImpact {...mainChartProps} />
        )}
        {/*// Hide wellbeing statistics */}
        {/*<WellbeingStatistics />*/}
        {showStakeholdersInGrid && (
          // Hide on filters: regional data lacks detailed impact pathway breakdown
          // During PNG export, this stays visible while a hidden copy is rendered in full-width
          // During PDF export, this is hidden and chart moves to full-width
          <SocialValueByStakeholders
            chartMode={stakeholdersChartMode}
            onChartModeChange={setStakeholdersChartMode}
            {...mainChartProps}
          />
        )}
        <ImpactByResidentialStatus />
        {/*<ImpactByDisability />*/}
        {/*<ImpactByIndexOfMultipleDeprivation />*/}
        <SocialValueByAgeGroup />
        {countryOfOperation === 'SAU' && hasMultipleRegions && (
          // Show Impact by Region chart only for Saudi Arabia and when there are multiple regions
          <ImpactByRegion />
        )}
        <GenderGraph />
      </div>
      <div className={classes.graphsContainer}>
        {/*container for hidden export copies*/}
        {showHeadcountChart && isPngExporting && (
          <HiddenExportCopy id={`${gridCharts.sroiHeadcountPerStakeholder}-export`}>
            <HeadcountAndPerStakeholderImpact {...pngChartProps} />
          </HiddenExportCopy>
        )}
      </div>
      <div id="sroi-full-width-container" className={classes.fullWidthGraphsContainer}>
        {showStakeholdersForPdf && (
          // During PDF export: chart shown here at full width (normal ID)
          <SocialValueByStakeholders
            chartMode={stakeholdersChartMode}
            onChartModeChange={setStakeholdersChartMode}
            {...mainChartProps}
          />
        )}
        {showStakeholdersForPng && (
          // During PNG export: render hidden copy for screenshot capture
          // Chart will call onChartReady() when rendering completes, which resolves
          // the Promise in exportChartAsPng, allowing safe screenshot capture
          // fullWidth={false} prevents forcing full width, letting chart size naturally to content
          <HiddenExportCopy id={`${gridCharts.sroiSocialValueStakeholders}-export`} fullWidth={false}>
            <SocialValueByStakeholders
              chartMode={stakeholdersChartMode}
              onChartModeChange={setStakeholdersChartMode}
              {...pngChartProps}
            />
          </HiddenExportCopy>
        )}
        {(selectedStakeholder || hasNoFiltersApplied) && <MappingOfImpactToSdgs />}
        {hasNoFiltersApplied && <MappingOfImpactToThemes />}
      </div>
    </>
  )
}

export default ReportContent
